import { BrowserRouter } from 'react-router-dom'
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthRouter } from './router/router';
import { createContext, useMemo, useState, useEffect } from 'react';
import Chart from 'chart.js/auto';
import {CategoryScale} from 'chart.js';
import ThemeWrapper from './common/components/ThemeWrapper';
import { AuthProvider } from './providers/AuthProvider';
import InactivityDetector from './common/components/InactivityDetector';
import AppUpdateToast from './common/components/AppUpdateToast';
import { PlanLimitDialog } from './components/design-system/plan/PlanLimitDialog';
import PushSync from './common/components/PushSync';
import ErrorBoundary from './common/components/ErrorBoundary';
import { ThemeTransitionOverlay } from './components/ThemeTransitionOverlay';
import { BatchProcessingProvider } from './contexts/BatchProcessingContext';
import { NotificationsProvider } from './providers/NotificationsProvider';
import { PresenceProvider } from './modules/presence/PresenceProvider';
import { AskAiProvider } from './modules/vendor/assistant/AskAiContext';
import { BatchProcessingPanel } from './components/BatchProcessingPanel';
import { useTranslation } from 'react-i18next';
import { isBookDemoPage } from './router/account-paths';
Chart.register(CategoryScale);

/** Import main fonts */
//import './common/fonts/fonts.js'

// Component to handle RTL direction and Arabic font
const LanguageDirectionHandler = () => {
    const { i18n } = useTranslation();

    useEffect(() => {
        const isArabic = i18n.language === 'ar';
        document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    return null;
};

export const ColorModeContext = createContext<{
    mode: 'light' | 'dark';
    toggleColorMode: () => void;
    isTransitioning: boolean;
}>({
    mode: 'light',
    toggleColorMode: () => {},
    isTransitioning: false
});

function App() {
    // "Book a demo" over a prospect's demo page is drawn on someone else's page (see isBookDemoPage).
    const [overlay] = useState(() => isBookDemoPage(window.location.pathname));

    // Get initial mode from localStorage or default to 'light'
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        // A frame is transparent only while its colour scheme matches the page
        // around it; a shop's page is light, so the overlay always is.
        if (overlay) return 'light';
        const savedMode = localStorage.getItem('colorMode');
        return (savedMode === 'dark' || savedMode === 'light') ? savedMode : 'light';
    });

    const [isTransitioning, setIsTransitioning] = useState(false);

    const colorMode = useMemo(
      () => ({
        mode,
        isTransitioning,
        toggleColorMode: () => {
          // Start transition
          setIsTransitioning(true);

          // Change mode after a brief delay
          setTimeout(() => {
            setMode((prevMode) => {
              const newMode = prevMode === 'light' ? 'dark' : 'light';
              localStorage.setItem('colorMode', newMode);
              return newMode;
            });
          }, 200);

          // End transition after 2 seconds
          setTimeout(() => {
            setIsTransitioning(false);
          }, 2000);
        },
      }),
      [mode, isTransitioning],
    );

    /*
     * The body's own colours.
     *
     * These used to be painted imperatively from the old palette, which is a
     * different set of values from the tokens every component now reads — so
     * the page behind the app could disagree with the app. Pointing them at
     * the tokens themselves keeps one source of truth, and the `.dark` class
     * below is what re-resolves them.
     */
    useEffect(() => {
        // The overlay has no background of its own: the shop's page shows through it.
        document.body.style.backgroundColor = overlay ? 'transparent' : 'var(--background)';
        document.body.style.color = 'var(--foreground)';
        document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    }, [mode, overlay]);

    /*
     * Tell Tailwind which mode we are in.
     *
     * shadcn's components carry `dark:` classes and the theme's dark palette
     * is defined under `.dark`, but the app's own switch only ever moved MUI's
     * palette — so every `dark:` rule in ui/ was dead. `color-scheme` comes
     * along for the ride so form controls and scrollbars follow too.
     */
    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle('dark', mode === 'dark');
        root.style.colorScheme = mode;
    }, [mode]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <LanguageDirectionHandler />
            <BrowserRouter>
                <AuthProvider>
                    <NotificationsProvider>
                        <PresenceProvider>
                        <BatchProcessingProvider>
                            {/*
                              * The assistant wraps the router rather than
                              * living on a page: "ask about this" is wanted on
                              * the page you are already on, and closing the
                              * catalogue to go and ask about the catalogue is
                              * the behaviour it exists to avoid.
                              */}
                            <TooltipProvider delayDuration={200}>
                            <AskAiProvider>
                                <ThemeWrapper>
                                    <InactivityDetector />
                                    <ErrorBoundary>
                                        <AuthRouter />
                                    </ErrorBoundary>
                                    <BatchProcessingPanel />
                                    {/*
                                      Bottom right, app-wide. Agent runs are
                                      durable and outlive the page that started
                                      them, so what finished has to be able to
                                      reach the vendor wherever they went.
                                    */}
                                    <AppUpdateToast />
                                    {/* Every plan limit a request runs into, said once and the same way. */}
                                    <PlanLimitDialog />
                                    <PushSync />
                                    <Toaster position="bottom-right" closeButton offset={{ bottom: 'calc(24px + var(--support-fab-clearance, 0px))' }} />
                                </ThemeWrapper>
                            </AskAiProvider>
                            </TooltipProvider>
                        </BatchProcessingProvider>
                        </PresenceProvider>
                    </NotificationsProvider>
                </AuthProvider>
            </BrowserRouter>

            {/* Theme Transition Blur Overlay */}
            <ThemeTransitionOverlay isTransitioning={isTransitioning} />
        </ColorModeContext.Provider>

    );
}
export default App;